import React, { useEffect, useState, useRef } from 'react';
import {
	Grid,
	Stack,
	Button,
	TextField,
	FormControlLabel,
	List,
	ListItem,
	ListItemText,
	Tooltip
} from '@mui/material';
import { getDatabricksJobs, databricksJobSearch, getDecodedToken, getDatabricksSectors, getDatabricksJobsDate, getWorkspaceList, getDatabricksJobsChart } from '../../../service/service';
import { dashboardUrl,databricksurl } from '../../../service/config';
import AnalyticsVisualization from '../../framework/analyticsVisualization';
import type { Column, FormatterProps } from 'react-data-grid';
import { CustomizedDataGrid } from '../../customized_data_grid';
import { useSearchParams } from 'react-router-dom';
import './DatabricksJobs.scss';
import Loader from '../../Loader/loader';
import moment from 'moment-timezone';
import { ReactComponent as DatePickerIcon } from '../../../assets/calendar-outline_white.svg';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Popover from '@mui/material/Popover';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import useStyles from '../../jobs/jobs_styles';
import { ReactComponent as FilterIcon } from '../../../assets/ml_summary_filter.svg';
import { ReactComponent as Downarrow } from '../../../assets/down-arrow.svg';
import axios from 'axios';
interface Row {
	project_name: string;
	workspace_name: string;
	cluster_name: string;
	job_name: string;
	runTimeM: number;
	status: string;
	job_trigger_type: string;
	job_startDate: string;
}

function formatRunTime(num: number): string | number {
  const hours = Math.floor(num / 60);
  const minutes = num % 60;

  if (num <= 0) {
    return 0;
  }

  let hoursText = '';

  if (hours) {
    if (hours > 1) {
      hoursText = `${hours} hrs`;
    } else {
      hoursText = `${hours} hr`;
    }
  }

  let minutesText = '';

  if (minutes) {
    if (minutes > 1) {
      minutesText = `${minutes.toFixed(1)} mins`;
    } else {
      minutesText = `${minutes.toFixed(1)} min`;
    }
  }

  return `${hoursText} ${minutesText}`.trim();
}

function updateDashboardClasses() {
	const url = globalThis.location.pathname;
	const activeId = document.getElementById('root');

	if (!activeId) {
		return;
	}

	if (url === '/databricks/jobs/dashboard') {
		activeId.classList.add('jobs-list-table');
		activeId.classList.remove('jobs-list', 'root');

		document.body.classList.add(
			'jobs-exe-datas',
			'jobs-list-datas',
			'databricks-jobs',
			'datefilter-align-sector'
		);

		document.body.classList.remove('jobs-run-table');
		return;
	}

	document.body.classList.remove(
		'jobs-list-datas',
		'jobs-run-table',
		'jobs-exe-datas'
	);

	activeId.classList.add('root');
	activeId.classList.remove(
		'jobs-list-table',
		'jobs-list'
	);
}

function getFilterLabel(filter: string | boolean): string {
  if (filter === true) {
    return 'Yes';
  }

  if (filter === false) {
    return 'No';
  }

  return String(filter);
}

type EllipsisFormatterProps = {
  readonly value?: string | null;
};

function EllipsisFormatter({
  value
}: EllipsisFormatterProps) {
  return (
	<div title={value ?? ''} className="has-ellipsis">
	  {value || '--'}
	</div>
  );
}

type StringRowKeys =
  | 'project_name'
  | 'workspace_name'
  | 'cluster_name'
  | 'job_name'
  | 'status';

function createEllipsisFormatter(field: StringRowKeys) {
  return function Formatter(props: FormatterProps<Row>) {
    return (
      <EllipsisFormatter
        value={props.row[field]}
      />
    );
  };
}

function renderErrorState(error: string | null) {
  const baseStyle = {
    color: '#e5e5e5',
    marginLeft: '25%',
    padding: '10px',
    fontWeight: 500,
    gridColumn: '1 / -1'
  };

  if (error === 'Cluster Inactive') {
    return (
      <div style={baseStyle}>
        Cluster is Inactive, it will take Max 2mins to start, Please try again later
      </div>
    );
  }

  if (error) {
    return (
      <div style={baseStyle}>
        Error occurred while fetching data. Please try again.
      </div>
    );
  }

  return null;
}

const workspaceNameFormatter = createEllipsisFormatter('workspace_name');
const clusterNameFormatter = createEllipsisFormatter('cluster_name');
const jobNameFormatter = createEllipsisFormatter('job_name');
const projectNameFormatter = createEllipsisFormatter('project_name');

function StatusFormatter(
  props: Readonly<FormatterProps<Row>>
) {
  const value = props.row.status;

  return (
    <div title={value ?? ''} className="has-ellipsis">
      {value || 'In Progress'}
    </div>
  );
}

const DatabrickJobs: React.FC = () => {

	const messagesEndRef = useRef<null | HTMLDivElement>(null);
	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
	}
	const columns: Array<Column<Row>> = [
			{
				key: 'project_name',
				name: 'Project Name',
				minWidth: 200,
				formatter: projectNameFormatter,
				width: 200
			},
			{
				key: 'workspace_name',
				name: 'Workspace',
				formatter: workspaceNameFormatter,
				minWidth: 170,
				width: 100
			},
			{
				key: 'cluster_name',
				name: 'Cluster Name',
				formatter: clusterNameFormatter,
				minWidth: 170,
				width: 100
			},
			{
				key: 'job_name',
				name: 'Job Name',
				formatter: jobNameFormatter,
				minWidth: 170,
				width: 250
			},
			{
				key: 'runTimeM',
				name: 'Job Run Time',
				minWidth: 150,
				width: 200
			},
			{
				key: 'status',
				name: 'Job Status',
				formatter: StatusFormatter,
				minWidth: 150,
				width: 200
			},
			{
				key: 'job_trigger_type',
				name: 'Trigger Type',
				minWidth: 150,
				width: 200
			},
			{
				key: 'job_startDate',
				name: 'Start Time',
				minWidth: 150,
				width: 200,
				formatter(props) {
					const startDate = props.row.job_startDate;
					if (startDate) {
						return moment(startDate).utc().format('MM-DD-YY HH:mm:ss');
					}
					return '';
				}
			}
		
		]
	const defaultColumnNames = [
		'Project Name',
		'Workspace',
		'Cluster Name',
		'Job Name',
		'Job Run Time',
		'Job Status',
		'Execution Time',
		'Trigger Type',
		'Start Time'
	];
	const dataFetchedRef = useRef(false);
	const [databricksJobList, setDatabricksJobList] = useState<Row[]>([]);
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(100);

	const [filterData, setFilterData] = useState<Row[]>([]);
	const [searchDetails, setFilterSearchInfo] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [filterInfo, setFilterInfo] = useState([]);
	const [drillDownStatusfilterInfo, setDrilldownStatusFilterInfo] = useState<Record<string, string>>({});
	const [searchParams, setSearchParams] = useSearchParams();
	const hasDrillDowned = searchParams.has('isDrillDowned');
	const dateFrom: string = searchParams.get('fromDate')?.substring(0, 10) as string;
	const dateTo: string = searchParams.get('toDate')?.substring(0, 10) as string;
	const startDt = moment(dateFrom).startOf('day').subtract(1, 'hours').format('YYYY-MM-DD HH:mm');
	const endDt = moment(dateTo).endOf('day').subtract(1, 'hours').format('YYYY-MM-DD HH:mm');
	const [fromDateTime, setFromDateTime] = useState<any>(
		searchParams.has('fromDate') ? startDt : null
	);
	const [toDateTime] = useState<any>(
	searchParams.has('toDate') ? endDt : null
	);

	// sector filter
	const userSector = getDecodedToken()?.sector?.name?.toLowerCase();
	const classes = useStyles();
	const [sectorOptionsList, setSectorOptionsList] = useState<any[]>([]);
	const [filterMenu, setFilterMenu] = useState<{
		mouseX: number;
		mouseY: number;
	} | null>(null);
	const [itemsChecked, setItemsChecked] = useState(false);
	const [checkedList, setCheckedList] = useState<any[]>([]);
	const [currSectorFilter, setCurrSectorFilter] = useState<any[]>([]);
	const [pastMonths, setPastMonths] = useState<{ label: string; value: string; }[]>([]);
	const generatePastMonths = () => {
		const currentDate = moment();
		const months = [];
		months.push({ label: 'This Month', value: currentDate.format('MMM') });
		setMonthFilter(currentDate.format('MMM'));
		for (let i = 1; i < 7; i++) {
			const pastMonth = currentDate.clone().subtract(i, 'months');
			months.push({ label: pastMonth.format('MMM \'YY'), value: pastMonth.format('MMM') });
    	}
    	setPastMonths(months);
  		};
		useEffect(() => {
			generatePastMonths();
		}, []);
	
	const [monthFilter, setMonthFilter] = useState('');

	const filterClick = (event: any) => {
		event.preventDefault();
		setFilterMenu(
			filterMenu === null
				? {
					mouseX: Number.parseInt(event.clientX) - 20,
					mouseY: Number.parseInt(event.clientY) - 6
				}
				: null
		);
	};
	const handleClose = (event: any) => {
		setFilterMenu(null);
	};
	const selectAllCheck = (e: any) => {
		const { checked } = e.target;
		const collection: any[] = [];
		if (checked) setCheckedList(sectorOptionsList);
		else setCheckedList([]);
		setItemsChecked(checked);
	};
	const handleChange = (e: any) => {
		const { value, checked } = e.target;
		const newlyChecked = [];
		if (checked && e.target.value) {
			newlyChecked.push(e.target.value);
		}
		if (checked) {
			setCheckedList([...checkedList, ...newlyChecked]);
			if(sectorOptionsList.length === (checkedList.length + newlyChecked.length)){
				setItemsChecked(true);
			}
		} else {
			setCheckedList(checkedList.filter((item) => item !== value));
			setItemsChecked(false);
		}
	};

	const handleOkClick = (event: any) => {		
	
		if (sectorOptionsList.length == checkedList.length) {
			setCheckedList([])
			localStorage.setItem('dj-summary-sector-filter', JSON.stringify([]));
		} else {
			localStorage.setItem('dj-summary-sector-filter', JSON.stringify(checkedList));
		}
		
		if (checkedList.length > 0) {
			setCurrSectorFilter(checkedList);
		} else {
			setCurrSectorFilter([]);
		}
		fetchWorkspaceList();
		databricksJobListingFunc(
			page,
			pageSize,
			sortingInfo,
			fromDateTime,
			[],
			checkedList,
			{}
		);
		handleClose(event);
	};
	async function fetchSectorOptionsData() {
		const sectorRes = await getDatabricksSectors('1', '100', '');
		if (sectorRes.success) {
			setSectorOptionsList(
				sectorRes.data.sector.map((x: any) => {
					if (x.name === 'S&T') {
						return 'SNT';
					}
					else if (x.name === 'Europe') {
						return 'EUROPE';
					}
					else if (x.name === 'Global') {
						return 'GLOBAL';
					}
					else {
						return x.name;
					}
				})
			);
		}
	}

	const handlePageChange = async (event: any, value: number) => {
	try {

		setLoading(true);
		let nextPage = value.toString(); 
		if(event.target.dataset.testid == "LastPageIcon" || event.target.dataset.testid == "FirstPageIcon"){
			nextPage = value.toString(); 
		}
		else if(event.target.dataset.testid == "NavigateBeforeIcon"){
			nextPage = (Number(page) - 1).toString();
		}
		else if (nextPage === event.target.textContent) { 
			nextPage = event.target.textContent;
		} else {
			nextPage = (Number(page) + 1).toString();
		}
		await databricksJobListingFunc(
			Number.parseInt(nextPage),
			pageSize,
			sortingInfo,
			fromDateTime,
			parseFilter(filterInfo),
			checkedList,
			{}
		)
		} catch(error: unknown) {
			setError(error instanceof Error ? error.message : 'Something went wrong'); // Set error state when API call fails
		} finally {
			setLoading(false);
		};
	};

	const handlepageSizeChange = async(event: any) => {
	try {

		setLoading(true);
		await databricksJobListingFunc(
			'1',
			event.target.value,
			sortingInfo,
			fromDateTime,
			parseFilter(filterInfo),
			checkedList,
			{}
		)
		} catch(error: unknown) {
			setError(error instanceof Error ? error.message : 'Something went wrong'); // Set error state when API call fails
		} finally {
			setLoading(false);
		};
	};

	const handleSorting = async(sortingInfo: any) => {
	try {

		setLoading(true);
		await databricksJobListingFunc(
			page,
			pageSize,
			sortingInfo,
			fromDateTime,
			parseFilter(filterInfo),
			checkedList,
			{}
		)
		} catch(error: unknown) {
			setError(error instanceof Error ? error.message : 'Something went wrong'); // Set error state when API call fails
		} finally {
			setLoading(false);
		};
	};

	const [jobClusterFailure, setJobClusterFailure] = useState<string[]>([]);

	const [selectedValueCluster, setSelectedValueCluster] = useState('');
	const [filterValueCluster, setFilterValueCluster] = useState('');
	const [jobClusterFailureAvailable, setJobClusterFailureAvailable] = useState(false);

	const handleFilterChangeCluster = (event: any) => {
		setFilterValueCluster(event.target.value);
	};

	const handleSelectCluster = (workspace: any) => {
		setSelectedValueCluster(workspace);
		setFilterMenuRight(null);
	};

	const filteredCluster = jobClusterFailure.filter((name) =>
		name.toLowerCase().includes(filterValueCluster.toLowerCase())
	);

	const [filterMenuRight, setFilterMenuRight] = useState<{
		mouseX: number;
		mouseY: number;
	} | null>(null);

	const filterClickRight = (event: any) => {
		event.preventDefault();
		setFilterMenuRight(
			filterMenuRight === null
				? {
					mouseX: Number.parseInt(event.clientX) - 20,
					mouseY: Number.parseInt(event.clientY) - 7
				}
				: null
		);
	};

	const handleCloseRight = (event: any) => {
		setFilterMenuRight(null);
	};

	const resetSearchParams = () => {

		if (searchParams.has('fromDate')) {
			searchParams.delete('status');
			searchParams.delete('scheduleDate');
			searchParams.delete('fromDate');
			setSearchParams(searchParams);
		} else if (hasDrillDowned) {
			setSearchParams(searchParams);
		}
	};

	const handleDateTimeChange = (field: any, dateTime: any) => {
		
		if (field === 'fromDateTime') setFromDateTime(dateTime);
		localStorage.setItem('processedDate', moment(dateTime).format('YYYY-MM-DD'));
		applyDateTimeRange(dateTime);
	};
	const applyDateTimeRange = async(fromDateTime: any) => {
	try {

		resetSearchParams();
		setLoading(true);
		fetchWorkspaceList();
		// reset column filter when date is applied
		await databricksJobListingDateFunc(
			"1",
			pageSize,
			sortingInfo,
			fromDateTime,
			[],
			checkedList,
			drillDownStatusfilterInfo
		)
		} catch(error: unknown) {
			setError(error instanceof Error ? error.message : 'Something went wrong'); // Set error state when API call fails
		} finally {
			setLoading(false);
		};
	};
	const handleFilter = async(filterInfo: any) => {
	try {

		setFilterInfo(filterInfo);
		setLoading(true);
		await databricksJobListingFunc(1, pageSize, sortingInfo, fromDateTime, parseFilter(filterInfo), checkedList, drillDownStatusfilterInfo)
		} catch(error: unknown) {
			setError(error instanceof Error ? error.message : 'Something went wrong'); // Set error state when API call fails
		} finally {
			setLoading(false);
		};
	};

	async function jobSearchForDatabrcksFunc(searchDetails: any, columnKey: any) {
		const res = await databricksJobSearch(searchDetails, columnKey, fromDateTime, parseFilter(filterInfo), checkedList, drillDownStatusfilterInfo);
		if (res.success) {
			setFilterSearchInfo(res.data);
		}
	}

	const handleSearch = (searchDetails: any, columnKey: any) => {
		console.log(searchDetails, columnKey)
		if(searchDetails?.length >= 3)
		jobSearchForDatabrcksFunc(searchDetails, columnKey);
	};

	const parseFilter = (filterInfo: any, name?: string) => {
		// Call only filterable columns and reset the filtering in case of wrong/empty column
		const parsedFilter: any = {};
		Object.keys(filterInfo).forEach((key: any) => {
			if (key !== name && filterInfo[key]?.length) {
				parsedFilter[key] = filterInfo[key];
			}
		});
		return parsedFilter;
	};

	// initial load API call
	async function databricksJobListingFunc(
		pageNumber: any,
		size: any,
		sortingInfo: any,
		fromDateTime: any,
		jobFilterInfo: any,
		sector: any,
		drillDownStatusFilter?: any
	) {
		setLoading(true);
	
		try {
			// Check if there's a stored sector filter in localStorage
			let storedSectorFilter = localStorage.getItem('dj-summary-sector-filter')
				? JSON.parse(localStorage.getItem('dj-summary-sector-filter') ?? '')
				: null;
			if (storedSectorFilter) {
				sector = storedSectorFilter;
			}
	
			// Make a call to Databricks
			const databricksresponse = await axios.get(`${databricksurl}`);
	
			if (databricksresponse?.status) {
			const res = await getDatabricksJobs(
				pageNumber.toString(),
				size.toString(),
				sortingInfo,
				jobFilterInfo,
				fromDateTime,
				sector,
				drillDownStatusFilter
			);

			if (res.success) {
				setTimeout(() => {
					console.log('scroll to table - ', hasDrillDowned)
					if (hasDrillDowned) {
						scrollToBottom()
					}
				}, 1000);
				if (res.data.data !== null) {
					const addDollar = res.data.data.map((item: any) => ({
						...item,
						runTimeM: item.runTimeM !== null && item.runTimeM !== undefined
								? formatRunTime(item.runTimeM)
								: '0',
						executionTimeInMIn: item.executionTimeInMIn !== null && item.executionTimeInMIn !== undefined
							? `${item.executionTimeInMIn.toFixed(2)}`
							: null
					}));

					setDatabricksJobList(addDollar);
					setPaginationData(res.data.page);
					setFilterData(res.data.filterOptions);
					setFilterInfo(jobFilterInfo);
					setPage(pageNumber);
					setPageSize(size);
					setFromDateTime(localStorage.getItem("processedDate"));
					setSortingInfo(sortingInfo);
						setError(null);
					}
				} else {
					setError('Failed to fetch data'); // Set the error state if API call fails
				}
			} else {
				setError('Cluster Inactive');
			}
		} catch (error: unknown) {
			console.error(error);
			setError('Failed to fetch data');
		} finally {
				setLoading(false);
		}
	}
	

	// User select specific date API call
	async function databricksJobListingDateFunc(
		pageNumber: any,
		size: any,
		sortingInfo: any,
		fromDateTime: any,
		jobFilterInfo: any,
		sector: any,
		drillDownStatusfilterInfo?: any
	) {
		setLoading(true);
		try {
			const res = await getDatabricksJobsDate(
				pageNumber.toString(),
				size.toString(),
				sortingInfo,
				jobFilterInfo,
				fromDateTime,
				sector,
				drillDownStatusfilterInfo
			);


			if (res.success) {
				console.log('this function will trigger from date selection only');
				if (res.data.data !== null) {
					const addDollar = res.data.data.map((item: any) => ({
						...item,
						runTimeM: `${item.runTimeM !== null && item.runTimeM !== undefined ? formatRunTime(item.runTimeM) : 0}`,
						executionTimeInMIn: `${item.executionTimeInMIn !== null && item.executionTimeInMIn !== undefined
							? `${item.executionTimeInMIn.toFixed(2)}`
							: null
							}`
					}));
					console.log('jobsres', res)
					setDatabricksJobList(addDollar);
					setPaginationData(res.data.page);
					setFilterData(res.data.filterOptions);
					setFilterInfo(jobFilterInfo);
					setPage(pageNumber);
					setPageSize(size);
					setSortingInfo(sortingInfo);
				}
			} else {
				setError('Failed to fetch data'); // Set the error state if API call fails
			}
			} catch (error: unknown) {
				console.error(error);
				setError('Failed to fetch data');
			} finally {
					setLoading(false);
			}
	}
	
	async function fetchClusterFailure() {
		const res = await getDatabricksJobsChart(fromDateTime);
		if (res.success) {
			if (localStorage.getItem('processedDate')) {
				setFromDateTime(moment(localStorage.getItem('processedDate')));
			} else {
				setFromDateTime(moment(res.data.processedDate));
				localStorage.setItem('processedDate', res.data.processedDate);
			}
			console.log("data", res.data.processedDate)
		}
	}

	async function fetchWorkspaceList() {
		const fromDate = localStorage.getItem('processedDate') ? localStorage.getItem('processedDate') : moment(fromDateTime).format('YYYY-MM-DD');
		let storedSectorFilter = localStorage.getItem('dj-summary-sector-filter')
			? JSON.parse(localStorage.getItem('dj-summary-sector-filter') ?? '')
			: currSectorFilter;
		const res = await getWorkspaceList(fromDate, storedSectorFilter);
		const workspace_name = res.data.workspace_name;
		const workspaceList = ['All Workspaces', ...workspace_name];
		setJobClusterFailure(workspaceList);
		if (workspaceList.length > 0) {
			setJobClusterFailureAvailable(true);
		} else {
			setJobClusterFailureAvailable(false);
		}
	}

	function getDrilldownFilter(status: string | null): Record<string, string> {
		const statusMap: Record<string, string> = {
			SuccessfulJobs: 'successfulJobs',
			FailedJobs: 'failedJobs',
			CanceledJobs: 'canceledJobs',
			NotExecutedJobs: 'notExecutedJobs',
			ExecutedJobs: 'executedJobs',
			InprogressJobs: 'inprogressJobs',
			LongRunningCompletedJobs: 'completedJobs',
			LongRunningInProgressJobs: 'progressJobs'
		};

		const key = status ? statusMap[status] : undefined;

		return key ? { [key]: 'true' } : {};
	}

	function initializeSectorFilter() {
	const storedSectorFilter = localStorage.getItem(
		'dj-summary-sector-filter'
	);

	if (!storedSectorFilter) {
		return;
	}

	const parsedFilter = JSON.parse(storedSectorFilter);

	setCheckedList(parsedFilter);
	setCurrSectorFilter(parsedFilter);
}

function loadDatabricksJobs(
	filterQuerySkeleton: Record<string, string>
) {
	if (hasDrillDowned) {
		return databricksJobListingFunc(
			page,
			pageSize,
			sortingInfo,
			fromDateTime,
			parseFilter(filterInfo),
			checkedList,
			parseFilter(filterQuerySkeleton)
		);
	}

	return databricksJobListingFunc(
		page,
		pageSize,
		sortingInfo,
		fromDateTime,
		parseFilter(filterInfo),
		checkedList,
		{}
	).catch(error => {
		setError(error);
	});
}

	useEffect(() => {
	fetchClusterFailure();
	fetchWorkspaceList();

	if (dataFetchedRef.current) {
		return;
	}

	dataFetchedRef.current = true;

	if (userSector === 'all') {
		fetchSectorOptionsData();
	}

	initializeSectorFilter();

	const filterQuerySkeleton = hasDrillDowned
		? getDrilldownFilter(searchParams.get('status'))
		: {};

	setDrilldownStatusFilterInfo(filterQuerySkeleton);

	loadDatabricksJobs(filterQuerySkeleton);

	updateDashboardClasses();
}, []);

	const truncateText = (text: string, maxLength: number) => {
		if (text.length > maxLength && text !== "All Workspaces") {
			return text.slice(0, maxLength) + '...';
		}
		return text;
	};

	let fromDate = moment(fromDateTime).format('YYYY-MM-DD');
	let toDate = moment(toDateTime).format('YYYY-MM-DD');
	return (
		<div className="dashboard-panel job-schedules-wrap">
			<div className="job-exexutions-dateFilter-div">
				{/* [Date Range Menu] */}
				<div style={{ display: 'flex', alignItems: 'center', padding: '-12px', marginTop: '-22px' }}>
					{/* condition for default Today  */}
					{<LocalizationProvider className="outerDateContainer" dateAdapter={AdapterMoment}>
						<DatePicker
							showToolbar={true}
							components={{
								OpenPickerIcon: DatePickerIcon
							}}
							maxDate={new Date()}
							PopperProps={{ placement: 'auto' }}

							className="dateFilterTD"

							value={moment(fromDateTime).tz('America/New_York')}

							onChange={(dateTime) => {
								handleDateTimeChange('fromDateTime', dateTime);
							}}
							renderInput={(params: any) => <TextField name="startDateTime" {...params} />}
						/>
					</LocalizationProvider>
					}
				</div>

				{/* Code for sector filter */}
				<Stack>
					{userSector === 'all' ? (
						<FilterIcon onClick={filterClick} className="sector-filter1" />
					) : (
						<div className="separeat_sector">Sector: {getDecodedToken()?.sector?.name}</div>
					)}
					<Popover
						open={filterMenu !== null}
						onClose={handleClose}
						className="filterContainer"
						anchorReference="anchorPosition"
						anchorPosition={filterMenu ? { top: filterMenu.mouseY, left: filterMenu.mouseX } : undefined}
					>
						<div className="text-filter-row">
							<div className="selectAll-sec custom-select-options">
								<MenuItem className="selectAll">
									<FormControlLabel
										className={classes.checkbox}
										label="All"
										control={
											<Checkbox
												color="success"
												value="all"
												checked={itemsChecked}
												onChange={(e) => {
													selectAllCheck(e);
												}}
											/>
										}
									/>
								</MenuItem>
								<div className="checkbox-sec">
									{sectorOptionsList?.map((filter: any) => (
										<MenuItem key={String(filter)}>
										<FormControlLabel
											className={classes.checkbox}
											label={getFilterLabel(filter)}
											control={
											<Checkbox
												color="success"
												value={filter}
												checked={
												(filter === true && checkedList.includes('true')) ||
												(filter === false && checkedList.includes('false')) ||
												checkedList.includes(filter)
												}
												onChange={handleChange}
											/>
											}
										/>
										</MenuItem>
									))}
								</div>
							</div>
							<div className="buttonSec">
								<Button variant="contained" className={classes.cancelBtn} onClick={handleClose}>
									cancel
								</Button>
								<Button variant="contained" className={classes.okBtn} onClick={handleOkClick}>
									ok
								</Button>
							</div>
						</div>
					</Popover>
				</Stack>
			</div>
			{/* <div className="dashboard-panel"> */}
			{location.pathname === '/databricks/jobs/dashboard' && (
				// Grafana
				<>
					<Stack>
						<AnalyticsVisualization
							params={{
								dashboardUrl: dashboardUrl.dataBricksjobs,
								className: 'dataBricksJobsIframeContainer',
								otherParams: {
									'var-fromDate': fromDate,
									'var-toDate': toDate,
									'var-sector': currSectorFilter
								},
								seamless: true
							}}
						/>
					</Stack>
					<div className="iframe-wrapper">
						<div className="left-iframe-div">
							<Stack>
							<div className="month-dropdown-wrap">
								<select className='month-dropdown' onChange={(e) => { console.log(e.target.value); setMonthFilter(e.target.value); }}>
									{pastMonths.map(month => (
										<option key={month.value} value={month.value}>{month.label}</option>
										))}
								</select>
							</div>
								<AnalyticsVisualization
									params={{
										dashboardUrl: dashboardUrl.dataBricksjobsWorkspaceFailure,
										className: 'dataBricksJobsFailureIframeContainer',
										otherParams: {
											'var-fromDate': fromDate,
											'var-toDate': toDate,
											'var-sector': currSectorFilter,
											'var-month': monthFilter
										},
										seamless: true
									}}
								/>
							</Stack>
						</div>
						<div className="right-iframe-div">
							<Stack>
								<div>
									{jobClusterFailureAvailable && jobClusterFailure.length > 0 && (
										<Tooltip title={selectedValueCluster || 'All Workspaces'} placement="top">
											<button
												type="button"
												onClick={filterClickRight}
												className="sectors-filterRight"
											>
												{truncateText(selectedValueCluster || 'All Workspaces', 10)}
												<span>
												<Downarrow className="right-down-arrow" />
												</span>
											</button>
										</Tooltip>
									)}
									<Popover
										open={filterMenuRight !== null}
										onClose={handleCloseRight}
										className="clusterFailureFilter"
										anchorReference="anchorPosition"
										anchorPosition={filterMenuRight ? { top: filterMenuRight.mouseY, left: filterMenuRight.mouseX } : undefined}
									>
										<div className="text-filter-row">
											<div className="selectAll-sec custom-select-options">
												<div>
													<input
														value={filterValueCluster}
														onChange={handleFilterChangeCluster}
														placeholder="Search..."
														className="selectAll-sec-input" // Add the class name to the input
													/>
												</div>
												<div className="checkbox-sec">
													<List>
														{filteredCluster.map((workspace: string) => (
															<ListItem
																key={workspace}
																onClick={() => handleSelectCluster(workspace)}
																className="Popover-listItem" // Add the class name to the ListItem component
															>
																<ListItemText primary={workspace} />
															</ListItem>
														))}
													</List>
												</div>
											</div>
										</div>
									</Popover>
								</div>
								<AnalyticsVisualization
									params={{
										dashboardUrl: dashboardUrl.dataBricksjobsClusterFailure,
										className: 'dataBricksJobsFailureIframeContainer',
										otherParams: {
											'var-filterCluster': selectedValueCluster,
											'var-fromDate': fromDate,
											'var-toDate': toDate,
											'var-sector': currSectorFilter
										},
										seamless: true
									}}
								/>
							</Stack>
						</div>
					</div>
				</>
			)}

			<div className="react-divJobs jobs-table-wrap">
				<div ref={messagesEndRef} />
				<p className="databricks_title">Job Details</p>

				{renderErrorState(error) ? (
					renderErrorState(error)
				) : (
					<Grid className="databricks_jobs_tabledata">
						{loading ? (
							<Loader />
							) : (
							<>
								<CustomizedDataGrid
								tableColumns={columns}
								tableData={databricksJobList}
								tableType="DBJobs"
								defaultColumnNames={defaultColumnNames}
								onSorting={handleSorting}
								currSorting={sortingInfo}
								filterDataOptions={filterData}
								onFilterCheck={handleFilter}
								currFilter={filterInfo}
								searchDataValues={searchDetails}
								onFilterSearch={handleSearch}
								page={page}
								pageSize={pageSize}
								paginationData={paginationData}
								listLength={databricksJobList?.length}
								handlepageSizeChange={handlepageSizeChange}
								handlePageChange={handlePageChange}
								isDateRangeFilter={true}
								isDisabled={false}
								/>

								{databricksJobList?.length ? (
								<p className="disclaimer">
									Note: Double hyphens (--) indicate that the field has no data.
									Job Run Time field - hr or min will display where number is less than 2
									and hrs or mins will display where number is greater than or equal to 2.
								</p>
								) : null}
							</>
						)}
					</Grid>
				)}
			</div>
		</div>
	);
};
export default DatabrickJobs;
